import VuexORM from "@vuex-orm/core";
import { createStore, type StoreOptions } from "vuex";
import { loaderModule } from "../../common/components/loader-bar/loader-module";
import { rootStoreConstant } from "../../common/constant/root-action-mutation-getter";
import { Enums } from "../../common/helper/enums";
import ServiceProviderServices from "../../common/services/service-provider-service";
import { ContactModule } from "../../contact/store/modules/contact-state-module";
import { documentModule } from "../../document/store/document-module";
import { EntityTickManager } from "../../infrastructure/entity-tick-manager";
import { ErrorTemplateBuilder } from "../../infrastructure/error-template-builder";
import { ticketDetailModule } from "../../ticket/store/ticket-detail-module";
import { ticketListModule } from "../../ticket/store/ticket-list-module";
import { MasterModule } from "../store/modules/master-module";
import { database } from "./register-database";
import { type RootState, StoreState } from "./state";

const store: StoreOptions<RootState> = {
  state: StoreState,

  modules: {
    ticketListModule,
    ticketDetailModule,
    documentModule,
    ContactModule,
    loaderModule,
    MasterModule,
  },

  plugins: [VuexORM.install(database)],
  getters: {
    [rootStoreConstant.getter.getSignalRBulbColor](state) {
      return state.signalRBulbColor;
    },

    [rootStoreConstant.getter.getEmailConfigurationExist](state) {
      return state.doesEmailConfigurationsExist;
    },

    isCustomDomainAdded(state): boolean {
      return state.isCustomDomainAdded;
    },

    getSignalRConnectionStatus(state): boolean {
      return state.isSignalRConnected;
    },

    getAccountNotExistStatus(state): boolean {
      return state.isAccountNotExist;
    },

    getServiceProviderId(state): number {
      return state.serviceProviderId;
    },

    getServiceProviderName(state): string {
      return state.serviceProviderName;
    },

    getServiceProviderPhone(state): string {
      return state.serviceProviderPhone;
    },

    getSupportEmail(state): string {
      return state.supportEmail;
    },
  },

  actions: {
    async getCustomDomainConfiguration({ commit }) {
      try {
        let response: any =
          await ServiceProviderServices.getCustomDomainConfiguration();

        let verifiedCustomDomainExists = false;
        if (response && response.length > 0) {
          response.forEach((e: any) => {
            if (
              e.emailConfigurationStatus != null &&
              e.emailConfigurationStatus ==
                Enums.EmailConfigurationStatus.CustomDomainVerified
            ) {
              verifiedCustomDomainExists = true;
            }
          });

          if (verifiedCustomDomainExists) {
            commit("SET_CUSTOMDOMAINSTATUS", true);
          } else {
            commit("SET_CUSTOMDOMAINSTATUS", false);
          }
        }
        return [];
      } catch (e) {
        commit("SET_CUSTOMDOMAINSTATUS", false);
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "store.ts -> getCustomDomainConfiguration",
            e,
          ),
        );
      }
    },

    async setEmailConfigurationExist({ commit }) {
      try {
        let result: any =
          await ServiceProviderServices.doesEmailConfigurationExist();
        commit(
          rootStoreConstant.mutation.SET_EMAIL_CONFIGURATIONS_EXIST,
          result,
        );
        return result;
      } catch (e) {
        throw new Error(
          ErrorTemplateBuilder.getCatchText(
            "Store.setEmailConfigurationExist",
            e,
          ),
        );
      }
    },

    async setSignalrConnectionStatus({ dispatch, commit }, payload: any) {
      return new Promise(async (resolve) => {
        commit("SET_SIGNALRCONNECTIONSTATUS", payload);

        commit(
          rootStoreConstant.mutation.SET_SIGNALR_BULB_COLOR,
          payload.connected == true ? "green" : "red",
        );

        if (payload.connected == true) {
          if (
            payload.reConnected != null &&
            payload.reConnected != undefined &&
            payload.reConnected == true
          ) {
            console.log("tick endpoints called on SignalR reconnection");
            EntityTickManager.updateStoreDataByTick();
          }
        } else if (payload.connectionOpen == false) {
          dispatch("MasterModule/ShowSnackbar", {
            isSuccess: false,
            message: "Application is now in offline mode",
            Timeout: 4000,
          });
        }
        resolve([]);
      });
    },

    async setAccountNotExistStatus({ commit }, payload: any) {
      return new Promise(() => {
        commit("SET_ACCOUNTNOTEXISTSTATUS", payload);
      });
    },
  },

  mutations: {
    [rootStoreConstant.mutation.SET_SIGNALR_BULB_COLOR](state, payload) {
      state.signalRBulbColor = payload;
    },
    SET_CUSTOMDOMAINSTATUS(state, payload) {
      state.isCustomDomainAdded = payload;
    },

    SET_SIGNALRCONNECTIONSTATUS(state, payload) {
      state.isSignalRConnected = payload.connected;
    },

    SET_ACCOUNTNOTEXISTSTATUS(state, payload) {
      state.isAccountNotExist = payload.isAccountNotExist;
    },

    Set_ServiceProviderId(state, serviceProviderId: number) {
      state.serviceProviderId = serviceProviderId;
    },

    Set_ServiceProviderName(state, serviceProviderName: string) {
      state.serviceProviderName = serviceProviderName;
    },

    Set_ServiceProviderPhone(state, serviceProviderPhone: string) {
      state.serviceProviderPhone = serviceProviderPhone;
    },

    Set_SupportEmail(state, supportEmail: string) {
      state.supportEmail = supportEmail;
    },
  },
};

export default createStore<RootState>(store);
